import '@/assets/styles/global.scss';
import '@/assets/styles/element-plus.scss';
import '@/assets/styles/bootstrap.scss';
import '@/assets/fontawesome/css/all.min.css';

import { createApp, ref } from 'vue';
import VueCookies from 'vue3-cookies';
import mitt, { Emitter, EventType } from 'mitt';
import { TroisJSVuePlugin } from 'troisjs';
import ElementPlus, { ElLoading } from 'element-plus';

import { createPinia } from 'pinia';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fac } from '@/assets/icons/fac';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import * as ElementPlusIconsVue from '@element-plus/icons-vue';
import vueClickOutsideElement from 'vue-click-outside-element';

import App from '@/App.vue';
import router from './router';
import { KEY_CURRENT_NAME, KEY_CURRENT_PRICE, KEY_CURRENT_STEP, KEY_IS_EDITOR, KEY_STEP_TEXT } from './utils/keys';
import i18n from '@/i18n';
import { initializeSession } from './services/api/sessionService';
import { useUserStore } from '@/stores/userStore';

// Vue App Setup
library.add(fas, far, fac);

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    eventBus: Emitter<Record<EventType, unknown>>;
  }
}

const pinia = createPinia();
const eventBus = mitt();
const app = createApp(App);

app.component('FontAwesomeIcon', FontAwesomeIcon);
app.use(pinia);
app.use(router);
app.use(i18n as typeof i18n);
app.use(VueCookies as typeof VueCookies, { expireTimes: '30d', secure: true, sameSite: 'Strict' });
app.config.globalProperties.eventBus = eventBus;
app.use(ElementPlus);
app.use(TroisJSVuePlugin);
app.use(vueClickOutsideElement);

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  if (key !== 'Camera' && key !== 'Box') {
    app.component(key, component);
  }
}


app.provide(KEY_IS_EDITOR, ref(false));
app.provide(KEY_STEP_TEXT, ref('firstStepDescription'));
app.provide(KEY_CURRENT_NAME, ref('S-Flex Cover'));
app.provide(KEY_CURRENT_PRICE, ref('235'));
app.provide(KEY_CURRENT_STEP, ref(1));

/** 🔁 Keep URL in sync once Pinia domain is set/changes */
const userStore = useUserStore(pinia);
userStore.$subscribe((_mutation, state: any) => {
  const suffix = state?.domain?.urlSuffix;
  if (!suffix) return;

  const r = router.currentRoute.value;
  if (r.query.supplier?.toString() !== suffix) {
    router.replace({ path: r.path, query: { ...r.query, supplier: suffix } });
    localStorage.setItem('supplier', suffix);
  }
});

/** Ensures authentication is handled before the app loads */
async function initializeApp(): Promise<void> {
  const loadingInstance = ElLoading.service({
    lock: true,
    background: 'rgba(255, 255, 255)',
  });

  try {
    await router.isReady();
    await initializeSession();

    const suffix = userStore.domain?.urlSuffix;
    if (suffix) {
      const r = router.currentRoute.value;
      if (r.query.supplier?.toString() !== suffix) {
        await router.replace({ path: r.path, query: { ...r.query, supplier: suffix } });
        localStorage.setItem('supplier', suffix);
      }
    }

    app.mount('#app');
  } catch (error) {
    console.error('Error during initialization:', error);
  } finally {
    loadingInstance.close();
  }
}

initializeApp();

export default app;
